import React, { useState, useCallback, useEffect } from 'react';
import { Skeleton } from '@mui/material';

interface ImageProps {
    src?: string;
    alt?: string;
    fallbackSrc?: string;
    variant?: 'square' | 'rounded' | 'circle';
    imgStyle?: React.CSSProperties;
    containerStyle?: React.CSSProperties;
    skeletonVariant?: 'rectangular' | 'circular' | 'rounded';
    loading?: 'lazy' | 'eager';
    width?: string | number;
    height?: string | number;
    className?: string;
}

const Image: React.FC<ImageProps> = ({
    src,
    alt = '',
    fallbackSrc = '/default-logo.png',
    variant = 'square',
    imgStyle,
    containerStyle,
    skeletonVariant = 'rectangular',
    loading = 'lazy',
    width,
    height,
    className,
}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [imgSrc, setImgSrc] = useState<string>(src ?? fallbackSrc);

    useEffect(() => {
        setImgSrc(src ?? fallbackSrc);
        setIsLoading(!!src);
    }, [src, fallbackSrc]);

    const handleLoad = useCallback(() => setIsLoading(false), []);

    const handleError = useCallback(() => {
        setImgSrc(fallbackSrc);
        setIsLoading(false);
    }, [fallbackSrc]);

    const resolvedBorderRadius =
        variant === 'circle' ? '50%' : variant === 'rounded' ? 8 : 0;
    const resolvedSkeletonVariant =
        variant === 'circle'
            ? 'circular'
            : variant === 'rounded'
            ? 'rounded'
            : skeletonVariant;

    return (
        <div
            style={{
                position: 'relative',
                display: 'inline-block',
                width,
                height,
                overflow: 'hidden',
                flexShrink: 0,
                borderRadius: resolvedBorderRadius,
                ...containerStyle,
            }}
        >
            {isLoading && (
                <Skeleton
                    variant={resolvedSkeletonVariant}
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        transform: 'none',
                        borderRadius: resolvedBorderRadius,
                    }}
                />
            )}
            <img
                src={imgSrc}
                alt={alt}
                loading={loading}
                className={className}
                style={{
                    display: 'block',
                    width: '100%',
                    height: '100%',
                    opacity: isLoading ? 0 : 1,
                    transition: 'opacity 0.2s ease',
                    borderRadius: resolvedBorderRadius,
                    ...imgStyle,
                }}
                onLoad={handleLoad}
                onError={handleError}
            />
        </div>
    );
};

export default Image;
